/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/prop-types */
import React, {
  Dispatch,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import moment, { Moment } from 'moment';
import { buildDatePicker, styleRangeDates } from './helpers';
import formatDates from './util/formatDates';

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface RangeDatePickerProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'single';
  className?: string;
  from: Moment | undefined;
  setFrom: Dispatch<SetStateAction<Moment>> | undefined;
  fromCalendarMoment: Moment | undefined;
  setFromCalendarMoment: Dispatch<SetStateAction<Moment | undefined>>;
  to: Moment | undefined;
  setTo: Dispatch<SetStateAction<Moment>> | undefined;
  toCalendarMoment: Moment | undefined;
  setToCalendarMoment: Dispatch<SetStateAction<Moment | undefined>>;
  selectableDates?: 'all' | 'future' | 'current/future' | 'current/past';
  activeSelection: 'start' | 'end' | '';
  setActiveSelection: Dispatch<SetStateAction<'start' | 'end' | ''>>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  focusedDay: Moment | null;
  setFocusedDay: Dispatch<SetStateAction<Moment | null>>;
}

export const RangeDatePickerBody = forwardRef<
  HTMLDivElement,
  PropsWithChildren<RangeDatePickerProps>
>(function RangeDatePickerBody(
  {
    variant,
    className,
    from,
    setFrom,
    fromCalendarMoment,
    setFromCalendarMoment,
    to,
    setTo,
    toCalendarMoment,
    setToCalendarMoment,
    selectableDates,
    setActiveSelection,
    activeSelection,
    open,
    setOpen,
    focusedDay,
    setFocusedDay,
    ...rest
  },
  ref
) {
  const [fromCalendar, setFromCalendar] = useState<Moment[][]>([]);
  const [toCalendar, setToCalendar] = useState<Moment[][]>([]);
  const [cellIsFocused, setCellIsFocused] = useState<boolean>(false);
  const calendarCellRef = useRef<any>(null);

  const handleSetFrom = (value: Moment) => {
    const today = moment();
    const tomorrow = moment().add(1, 'days');
    if (setFrom) {
      if (selectableDates === 'future') {
        if (!value.isBefore(tomorrow, 'day')) {
          setFrom(value);
        }
      } else if (selectableDates === 'current/future') {
        if (!value.isBefore(today, 'day')) {
          setFrom(value);
        }
      } else if (selectableDates === 'current/past') {
        if (!value.isAfter(today, 'day')) {
          setFrom(value);
        }
      } else {
        setFrom(value);
      }
    }
  };

  const handleSetTo = (value: Moment) => {
    const today = moment();
    if (setTo) {
      if (value.isAfter(from, 'day')) {
        if (
          selectableDates === 'current/past' &&
          !value.isAfter(today, 'day')
        ) {
          setTo(value);
        } else if (selectableDates !== 'current/past') {
          setTo(value);
        }
      }
    }
  };

  const getNextMonthAsString = (left: Moment, right: Moment) => {
    if (left.format('MMMM') === right.format('MMMM'))
      return moment(left).add(1, 'month').format('MMMM');

    return moment(right).format('MMMM');
  };

  const handleDateSelectionKB = (day: Moment) => {
    activeSelection === 'start' ? handleSetFrom(day) : handleSetTo(day);
  };

  const setPreviousDay = (day: Moment) => {
    const previousDay = day.subtract(1, 'days');
    setFocusedDay(previousDay);
  };
  const setNextDay = (day: Moment) => {
    const nextDay = day.add(1, 'days');
    setFocusedDay(nextDay);
  };
  const setPreviousWeek = (day: Moment) => {
    const previousWeek = day.subtract(1, 'weeks');
    setFocusedDay(previousWeek);
  };
  const setNextWeek = (day: Moment) => {
    const nextWeek = day.add(1, 'weeks');
    setFocusedDay(nextWeek);
  };

  const handleCalendarKeyPress = (e: any, day: Moment) => {
    const key = e.key;
    const momentDay = moment(day); // day is wrapped in moment, styling functionalities won't work if moment wrapper is removed.
    switch (key) {
      case 'Enter': // Enter
        e.preventDefault();
        handleDateSelectionKB(momentDay);
        setActiveSelection('end');
        return;
      case 'Escape': // Esc
        setOpen(false);
        return;
      case 'Esc': // Esc
        setOpen(false);
        return;
      case ' ': // Space
        e.preventDefault();
        handleDateSelectionKB(momentDay);
        setActiveSelection('end');
        return;
      case 'Spacebar': // Space
        e.preventDefault();
        handleDateSelectionKB(momentDay);
        setActiveSelection('end');
        return;
      case 'ArrowLeft': // left
        e.preventDefault();
        setPreviousDay(momentDay);
        return;
      case 'ArrowUp': // up
        e.preventDefault();
        setPreviousWeek(momentDay);
        return;
      case 'ArrowRight': // right
        e.preventDefault();
        setNextDay(momentDay);
        return;
      case 'ArrowDown': // down
        e.preventDefault();
        setNextWeek(momentDay);
        return;
    }
  };

  useEffect(() => {
    if (from && to) {
      setFocusedDay(from);
    } else {
      setFocusedDay(moment());
    }
  }, []);

  useEffect(() => {
    if (fromCalendarMoment)
      setFromCalendar(buildDatePicker(fromCalendarMoment));
  }, [fromCalendarMoment, setFromCalendar]);

  useEffect(() => {
    if (toCalendarMoment) {
      setToCalendar(buildDatePicker(toCalendarMoment));
    }
    if (
      toCalendarMoment &&
      fromCalendarMoment &&
      toCalendarMoment!.format('MMMM') === fromCalendarMoment!.format('MMMM')
    ) {
      setToCalendar(buildDatePicker(toCalendarMoment));
    }
  }, [toCalendarMoment, setToCalendar, formatDates]);

  useEffect(() => {
    if (from) {
      const fromVal = from?.clone().add(1, 'days');
      if (from?.isAfter(to, 'day') && setTo) {
        setTo(fromVal);
      }
    }
  }, [from]);

  useEffect(() => {
    if (from) {
      const fromVal = from?.clone().add(1, 'days');
      if (from?.isAfter(to, 'day') && setTo) {
        setTo(fromVal);
      }
    }
  }, [from]);

  // fired on every re-render, keeps the accuracy of the cell that should be focused when using KB navigation
  useEffect(() => {
    if (open && calendarCellRef.current && cellIsFocused)
      calendarCellRef.current.focus();
  });

  // for keyboard navigation functionality when navigating between cells in different months
  useEffect(() => {
    if (variant === 'single') {
      if (focusedDay?.isAfter(fromCalendarMoment, 'month')) {
        setFromCalendarMoment(focusedDay);
      }
    } else {
      if (focusedDay?.isAfter(fromCalendarMoment, 'month')) {
        setToCalendarMoment(focusedDay);
      }
      if (focusedDay?.isBefore(fromCalendarMoment, 'month')) {
        setFromCalendarMoment(focusedDay);
      }
    }
  }, [focusedDay]);

  const renderDefaultRangeBody = !variant && (
    <div ref={ref} className="flex w-full justify-start items-start">
      <div className="w-285px">
        <div
          className={`w-285px px-5 pt-0 transition-all ${className || ''}`}
          {...rest}
        >
          <div className="w-full flex justify-between items-center gap-x-3 mt-4 mb-3">
            {daysOfWeek.map((d: string, i: number) => (
              <div
                className="font-open-sans flex items-center justify-around w-full text-thryv-gray-medium-500 font-normal text-day-of-week"
                key={i}
              >
                {d}
              </div>
            ))}
          </div>
          <>
            {fromCalendar.map((week: Moment[], i: number) => (
              <div
                key={i}
                className="w-full flex justify-start items-center gap-3 mt-2 mb-2"
              >
                {week.map((day: Moment, j: number) => {
                  return (
                    <div
                      key={j}
                      onClick={() => {
                        if (
                          day?.format('MMMM') ===
                          fromCalendarMoment?.format('MMMM')
                        ) {
                          if (activeSelection === 'start') {
                            handleSetFrom(day);
                            setActiveSelection('end');
                          } else {
                            handleSetTo(day);
                            setActiveSelection('end');
                          }
                        }
                      }}
                      className="w-full h-[25px]"
                      role="button"
                      ref={
                        focusedDay?.isSame(day, 'day') &&
                        fromCalendarMoment?.isSame(day, 'month')
                          ? calendarCellRef
                          : undefined
                      }
                      tabIndex={
                        focusedDay?.isSame(day, 'day') &&
                        fromCalendarMoment?.isSame(day, 'month')
                          ? 0
                          : -1
                      }
                      onFocus={() => setCellIsFocused(true)}
                      onBlur={() => setCellIsFocused(false)}
                      onKeyDown={(e) => handleCalendarKeyPress(e, day)}
                    >
                      {from && (
                        <span
                          className={`${styleRangeDates(
                            moment(day),
                            moment(from),
                            moment(to),
                            selectableDates,
                            activeSelection,
                            fromCalendarMoment?.format('MMMM')
                          )} font-open-sans font-semibold text-calendar-date flex justify-around w-full h-full items-center`}
                        >
                          {moment(day).format('D')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        </div>
      </div>

      <div className="w-285px">
        <div
          className={`w-285px px-5 pt-0 transition-all ${className || ''}`}
          {...rest}
        >
          <div className="w-full flex justify-between items-center gap-x-3 mt-4 mb-3">
            {daysOfWeek.map((d: string, i: number) => (
              <div
                className="font-open-sans flex items-center justify-around w-full text-thryv-gray-medium-500 font-normal text-day-of-week"
                key={i}
              >
                {d}
              </div>
            ))}
          </div>
          <>
            {toCalendar.map((week: Moment[], i: number) => (
              <div
                key={i}
                className="w-full flex justify-start items-center gap-3 mt-2 mb-2"
              >
                {week.map((day: Moment, j: number) => {
                  return (
                    <div
                      key={j}
                      onClick={() => {
                        if (
                          fromCalendarMoment &&
                          toCalendarMoment &&
                          day?.format('MMMM') ===
                            getNextMonthAsString(
                              fromCalendarMoment,
                              toCalendarMoment
                            )
                        ) {
                          if (activeSelection === 'end') {
                            handleSetTo(day);
                            setActiveSelection('end');
                          } else {
                            handleSetFrom(day);
                            setActiveSelection('end');
                          }
                        }
                      }}
                      className="w-full h-[25px]"
                      ref={
                        focusedDay?.isSame(day, 'day') &&
                        toCalendarMoment?.isSame(day, 'month')
                          ? calendarCellRef
                          : undefined
                      }
                      tabIndex={
                        focusedDay?.isSame(day, 'day') &&
                        toCalendarMoment?.isSame(day, 'month')
                          ? 0
                          : -1
                      }
                      onFocus={() => setCellIsFocused(true)}
                      onBlur={() => setCellIsFocused(false)}
                      onKeyDown={(e) => handleCalendarKeyPress(e, day)}
                    >
                      {from && to && fromCalendarMoment && toCalendarMoment && (
                        <span
                          className={`${styleRangeDates(
                            moment(day),
                            moment(from),
                            moment(to),
                            selectableDates,
                            activeSelection,
                            getNextMonthAsString(
                              fromCalendarMoment,
                              toCalendarMoment
                            )
                          )} font-open-sans font-semibold text-calendar-date flex justify-around w-full h-full items-center`}
                        >
                          {moment(day).format('D')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        </div>
      </div>
    </div>
  );

  const renderSingleRangeBody = variant === 'single' && (
    <div ref={ref} className="flex w-full justify-start items-start">
      <div className="w-285px">
        <div
          className={`w-285px px-5 pt-0 transition-all ${className || ''}`}
          {...rest}
        >
          <div className="w-full flex justify-between items-center gap-x-3 mt-4 mb-3">
            {daysOfWeek.map((d: string, i: number) => (
              <div
                className="font-open-sans flex items-center justify-around w-full text-thryv-gray-medium-500 font-normal text-day-of-week"
                key={i}
              >
                {d}
              </div>
            ))}
          </div>
          <>
            {fromCalendar.map((week: Moment[], i: number) => (
              <div
                key={i}
                className="w-full flex justify-start items-center gap-3 mt-2 mb-2"
              >
                {week.map((day: Moment, j: number) => {
                  return (
                    <div
                      key={j}
                      onClick={() => {
                        if (
                          day?.format('MMMM') ===
                          fromCalendarMoment?.format('MMMM')
                        ) {
                          if (activeSelection === 'start') {
                            handleSetFrom(day);
                            setActiveSelection('end');
                          } else {
                            handleSetTo(day);
                            setActiveSelection('end');
                          }
                        }
                      }}
                      className="w-full h-[25px]"
                      ref={
                        focusedDay?.isSame(day, 'day') &&
                        fromCalendarMoment?.isSame(day, 'month')
                          ? calendarCellRef
                          : undefined
                      }
                      tabIndex={
                        focusedDay?.isSame(day, 'day') &&
                        fromCalendarMoment?.isSame(day, 'month')
                          ? 0
                          : -1
                      }
                      onFocus={() => setCellIsFocused(true)}
                      onBlur={() => setCellIsFocused(false)}
                      onKeyDown={(e) => handleCalendarKeyPress(e, day)}
                    >
                      {
                        <span
                          className={`${styleRangeDates(
                            moment(day),
                            moment(from),
                            moment(to),
                            selectableDates,
                            activeSelection,
                            fromCalendarMoment?.format('MMMM')
                          )} font-open-sans font-semibold text-calendar-date flex justify-around w-full h-full items-center`}
                        >
                          {moment(day).format('D')}
                        </span>
                      }
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {renderDefaultRangeBody}
      {renderSingleRangeBody}
    </>
  );
});
